import {
  type SelectroTemplates,
  type SelectroText,
  type SelectroV2Props,
} from '@aha-app/selectro-v2';

export interface selectroPresenterBasePropsParams {
  serverSearch?: boolean;
}

/**
 * Base class for Selectro presenters to enforce implementation of methods in subclasses.
 */
class BaseSelectroPresenter {
  private static readonly _instances = new Map<
    Function,
    BaseSelectroPresenter
  >();

  // We store all the presenter instances in a static map to enforce singleton behavior
  static getInstance<T extends BaseSelectroPresenter>(this: new () => T): T {
    if (!BaseSelectroPresenter._instances.has(this)) {
      BaseSelectroPresenter._instances.set(this, new this());
    }
    return BaseSelectroPresenter._instances.get(this) as T;
  }

  baseTemplates(): SelectroTemplates {
    throw new Error('Must implement baseTemplates in subclass');
  }

  baseText(): SelectroText {
    throw new Error('Must implement baseText in subclass');
  }

  /**
   * By default, this is more than enough for most presenters.
   * If the record types need more customization, the method can be overridden.
   */
  baseProps(params?: selectroPresenterBasePropsParams): SelectroV2Props {
    return {
      templates: this.baseTemplates(),
      text: this.baseText(),
      ...(params?.serverSearch ? this.serverSearchProps() : {}),
    };
  }

  /**
   * Required props for SelectroV2 components that use server-side searching.
   */
  serverSearchProps(): Partial<SelectroV2Props> {
    return {
      usesServerSearch: true,
      fetchSearchParam: 'q',
    };
  }
}

export default BaseSelectroPresenter;
